/**
 * Admin API client - minimalna kopija POS klijenta.
 * Razlika: koristi adminAuthToken iz localStorage umesto Zustand store-a.
 */

const BASE = '/api';

class ApiError extends Error {
  status: number;
  data: any;
  constructor(status: number, data: any) {
    super(data?.message || data?.error || `HTTP ${status}`);
    this.status = status;
    this.data = data;
  }
}

async function request<T>(path: string, opts: RequestInit & { token?: string } = {}): Promise<T> {
  const { token, headers, body, ...rest } = opts;
  const finalToken = token ?? localStorage.getItem('admin-token');
  // Only send Content-Type when we actually have a JSON body - otherwise
  // Fastify rejects empty-body requests (e.g. DELETE) with FST_ERR_CTP_EMPTY_JSON_BODY.
  const hasBody = body !== undefined && body !== null;
  const res = await fetch(`${BASE}${path}`, {
    ...rest,
    body,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(finalToken ? { Authorization: `Bearer ${finalToken}` } : {}),
      ...headers,
    },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  // Auto-logout on 401 - token expired or invalid.
  // Skip for the login endpoint itself (wrong password should not wipe storage).
  if (res.status === 401 && !path.includes('/admin/auth/login')) {
    localStorage.removeItem('admin-token');
    localStorage.removeItem('admin-session');
    // Reload so App.tsx re-reads loadSession() and shows LoginScreen
    if (typeof window !== 'undefined') window.location.reload();
  }

  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export const api = {
  get: <T>(path: string, token?: string) => request<T>(path, { method: 'GET', token }),
  post: <T>(path: string, body?: any, token?: string) =>
    request<T>(path, { method: 'POST', token, body: body !== undefined ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: any, token?: string) =>
    request<T>(path, { method: 'PUT', token, body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: any, token?: string) =>
    request<T>(path, { method: 'PATCH', token, body: body !== undefined ? JSON.stringify(body) : undefined }),
  del: <T>(path: string, token?: string) => request<T>(path, { method: 'DELETE', token }),
};

export { ApiError };
